import React from "react";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiClockOutline, mdiCogOutline, mdiPauseCircleOutline } from "@mdi/js";
import { MemberAvatar } from "components";
import { useElapsedTime } from "shared/hooks";
import { TaskStatus, TeamTaskSubtaskDTO } from "shared/services";
import { formatClock, formatDuration } from "../taskBoard.utils";
import { TaskBoardState, TaskItemViewModel } from "../types";
import styles from "../styles.module.scss";
import { ProgressBar } from "./ProgressBar";
import { SubtaskReviewPanel } from "./SubtaskReviewPanel";

interface TaskItemCardProps {
	item: TaskItemViewModel;
	workspaceId?: string | null;
	taskId?: string | null;
	state?: TaskBoardState;
}

const statusIconByStatus: Record<TaskStatus, string> = {
	pending: mdiClockOutline,
	processing: mdiCogOutline,
	completed: mdiCheckCircleOutline,
	failed: mdiAlertCircleOutline,
	paused: mdiPauseCircleOutline,
	cancelled: mdiAlertCircleOutline,
};

const statusChipClassByStatus: Record<TaskStatus, string> = {
	pending: styles.statusChipPending,
	processing: styles.statusChipProcessing,
	completed: styles.statusChipCompleted,
	failed: styles.statusChipFailed,
	paused: styles.statusChipPaused,
	cancelled: styles.statusChipFailed,
};

const statusIconClassByStatus: Record<TaskStatus, string> = {
	pending: styles.statusIconPending,
	processing: styles.statusIconProcessing,
	completed: styles.statusIconCompleted,
	failed: styles.statusIconFailed,
	paused: styles.statusIconPaused,
	cancelled: styles.statusIconFailed,
};

export const TaskItemCard: React.FC<TaskItemCardProps> = React.memo(({ item, workspaceId, taskId, state }) => {
	const subtasks: TeamTaskSubtaskDTO[] = React.useMemo(() => {
		if (!state) return [];
		const node = state.taskItemById[item.id];
		if (!node) return [];
		return node.subtaskIds
			.map((id) => state.subtaskById[id])
			.filter((s): s is TeamTaskSubtaskDTO => Boolean(s));
	}, [state, item.id]);

	// Tempo decorrido em tempo real do item (entre started e completed, ou agora se ativo).
	const elapsed = useElapsedTime(item.startedAt, item.completedAt, 1000);

	return (
		<article className={`${styles.taskItemCard} ${item.errorDescription ? styles.taskItemCardError : ""}`}>
			<div className={styles.taskCol}>
				<div className={styles.taskTitleRow}>
					<h3 className={styles.taskTitle}>{item.title}</h3>
					<span className={`${styles.statusChip} ${statusChipClassByStatus[item.status]}`}>{item.statusLabel}</span>
				</div>
				<p className={styles.taskDescription}>{item.description}</p>
			</div>

			<div className={styles.status}>
				<div className={styles.progressCol}>
					<div className={styles.progressMainRow}>
						<ProgressBar
							value={item.progress}
							status={item.status}
						/>
						<Icon
							path={statusIconByStatus[item.status]}
							size={0.7}
							className={statusIconClassByStatus[item.status]}
						/>
						<span className={styles.progressPercent}>{item.progress}%</span>
					</div>
				</div>

				<div className={styles.agentCol}>
					<MemberAvatar
						id={item.activeMemberId ?? item.id}
						name={item.activeMemberName}
						size="sm"
						status={item.status}
					/>
					<span className={styles.agentName}>{item.activeMemberName}</span>
				</div>

				<div className={styles.timeCol}>
					<span className={styles.timeValue}>
						{formatClock(item.startedAt)} | {formatClock(item.completedAt)}
					</span>
					<span className={styles.timeDuration}>
						{item.startedAt ? elapsed : "—"}
					</span>
				</div>
			</div>

			{workspaceId && taskId && state && subtasks.length > 0 && (
				<SubtaskReviewPanel
					workspaceId={workspaceId}
					taskId={taskId}
					subtasks={subtasks}
					memberNamesById={state.memberNamesById}
					toolCallBySubtaskId={state.toolCallBySubtaskId}
				/>
			)}
		</article>
	);
});

TaskItemCard.displayName = "TaskItemCard";
